import { getContactAndTravelerMachineState } from "@apac/checkout";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  BookingQuestionId,
  type ActivityLanguageGuide,
  type BookingQuestion,
} from "@b2bportal/rbc-activities-api";
import {
  ParentState,
  getCartFulfillMachine,
  getCartQuoteMachine,
  getCartUpdateMachine,
  Event,
  getCardPaymentMachine,
  LodgingActionTypes,
  CardPaymentActionTypes,
  CartQuoteActionTypes,
  getReviewMachineState,
  GenericChildState,
  setContextWithKey,
  type TravelerBookingQuestionAnswer,
  PaymentInformationState,
  trackPollSuccess,
  trackPollFailure,
} from "@hopper-b2b/checkout";
import { ActivitiesActions } from "@hopper-b2b/checkout";
import { assign, type MachineConfig } from "xstate";
import { getRbcContactAndTravelerStateOverride } from "../checkout/states/ContactAndTraveler/stateOverride";
import { ActivityGuards } from "./guards";
import { getInitialActivitiesMachineContext } from "./initialContext";
import { initializeState } from "./initializeState";
import { getCompleteBuyActivityAdditionalProperties } from "./selectors";
import type { ActivitiesMachineContext } from "./types";
import {
  BookingConfirmationActionTypes,
  getRewardsPaymentMachine,
  type TEvent,
  getTermsAndConditionsMachineState,
} from "@/modules/checkout";
import { COMPLETE_BUY_ACTIVITY } from "@/utils/trackingEvents";

export enum ActivityInformationEvent {
  SET_SELECTED_LANGUAGE_GUIDE = "SET_SELECTED_LANGUAGE_GUIDE",
  SET_SPECIAL_REQUIREMENT = "SET_SPECIAL_REQUIREMENT",
  SET_SELECTED_PICKUP_POINT = "SET_SELECTED_PICKUP_POINT",
  SET_PICKUP_LOCATION_ANSWER = "SET_PICKUP_LOCATION_ANSWER",
  SET_CUSTOM_PICKUP_ADDRESS = "SET_CUSTOM_PICKUP_ADDRESS",
  SET_TRAVELER_BOOKING_QUESTION_ANSWER = "SET_TRAVELER_BOOKING_QUESTION_ANSWER",
  SET_PRIMARY_PARTICIPANT = "SET_PRIMARY_PARTICIPANT",
  SET_TRANSFER_ARRIVAL_MODE = "SET_TRANSFER_ARRIVAL_MODE",
  SET_TRANSFER_DEPARTURE_MODE = "SET_TRANSFER_DEPARTURE_MODE",
  SET_TRANSFER_ARRIVAL_OTHER_TEXT = "SET_TRANSFER_ARRIVAL_OTHER_TEXT",
  SET_TRANSFER_DEPARTURE_OTHER_TEXT = "SET_TRANSFER_DEPARTURE_OTHER_TEXT",
  SET_TRANSFER_CONDITIONAL_ANSWER = "SET_TRANSFER_CONDITIONAL_ANSWER",
}

export interface SetSelectedLanguageGuideEvent {
  type: ActivityInformationEvent.SET_SELECTED_LANGUAGE_GUIDE;
  data: ActivityLanguageGuide;
}

export interface SetSpecialRequirementEvent {
  type: ActivityInformationEvent.SET_SPECIAL_REQUIREMENT;
  data: string;
}

export interface SetSelectedPickupPointEvent {
  type: ActivityInformationEvent.SET_SELECTED_PICKUP_POINT;
  data: string | undefined;
}

export interface PickupLocationAnswer {
  answer: string;
  bookingQuestionId: "PickupPoint";
  unit: "LOCATION_REFERENCE";
}

export interface SetPickupLocationAnswerEvent {
  type: ActivityInformationEvent.SET_PICKUP_LOCATION_ANSWER;
  data: PickupLocationAnswer | undefined;
}

export interface SetCustomPickupAddressEvent {
  type: ActivityInformationEvent.SET_CUSTOM_PICKUP_ADDRESS;
  data: {
    hotelName?: string;
    address?: string;
    city?: string;
    zipCode?: string;
  };
}

export interface SetTravelerBookingQuestionAnswerEvent {
  type: ActivityInformationEvent.SET_TRAVELER_BOOKING_QUESTION_ANSWER;
  data: {
    bookingQuestionId: BookingQuestionId;
    answer: string;
    travelerId: string;
    unit?: string;
  };
}

export interface SetPrimaryParticipantEvent {
  type: ActivityInformationEvent.SET_PRIMARY_PARTICIPANT;
  primaryParticipantId: string;
}

export interface SetTransferArrivalModeEvent {
  type: ActivityInformationEvent.SET_TRANSFER_ARRIVAL_MODE;
  data: string;
}

export interface SetTransferDepartureModeEvent {
  type: ActivityInformationEvent.SET_TRANSFER_DEPARTURE_MODE;
  data: string;
}

export interface SetTransferArrivalOtherTextEvent {
  type: ActivityInformationEvent.SET_TRANSFER_ARRIVAL_OTHER_TEXT;
  data: string;
}

export interface SetTransferDepartureOtherTextEvent {
  type: ActivityInformationEvent.SET_TRANSFER_DEPARTURE_OTHER_TEXT;
  data: string;
}

export interface SetTransferConditionalAnswerEvent {
  type: ActivityInformationEvent.SET_TRANSFER_CONDITIONAL_ANSWER;
  data: {
    questionId: BookingQuestionId;
    answer: string;
  };
}

export type ActivityInformationEvents =
  | SetSelectedLanguageGuideEvent
  | SetSpecialRequirementEvent
  | SetSelectedPickupPointEvent
  | SetPickupLocationAnswerEvent
  | SetCustomPickupAddressEvent
  | SetTravelerBookingQuestionAnswerEvent
  | SetPrimaryParticipantEvent
  | SetTransferArrivalModeEvent
  | SetTransferDepartureModeEvent
  | SetTransferArrivalOtherTextEvent
  | SetTransferDepartureOtherTextEvent
  | SetTransferConditionalAnswerEvent;

const ActivityInformationActions = {
  setSelectedLanguageGuide: assign(
    (ctx: ActivitiesMachineContext, event: SetSelectedLanguageGuideEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.selectedLangGuide`,
        event.data
      )
  ),
  setSpecialRequirement: assign(
    (ctx: ActivitiesMachineContext, event: SetSpecialRequirementEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.specialRequirement`,
        event.data
      )
  ),
  setSelectedPickupPoint: assign(
    (ctx: ActivitiesMachineContext, event: SetSelectedPickupPointEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.selectedPickupPoint`,
        event.data
      )
  ),
  setPickupLocationAnswer: assign(
    (ctx: ActivitiesMachineContext, event: SetPickupLocationAnswerEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.pickupLocationAnswer`,
        event.data
      )
  ),
  setCustomPickupAddress: assign(
    (ctx: ActivitiesMachineContext, event: SetCustomPickupAddressEvent) =>
      setContextWithKey(ctx, ParentState.activityInformation, {
        ...ctx[ParentState.activityInformation],
        ...event.data,
      })
  ),
  setTravelerBookingQuestionAnswer: assign(
    (
      ctx: ActivitiesMachineContext,
      event: SetTravelerBookingQuestionAnswerEvent
    ) => {
      const currentAnswers =
        ctx[ParentState.activityInformation]?.travelerBookingQuestionAnswers ??
        [];
      const { bookingQuestionId, answer, travelerId } = event.data;
      const selectedTravelerIds =
        ctx[ParentState.travelerInformation]?.selectedTravelerIds ?? [];
      const idx = selectedTravelerIds.indexOf(travelerId);
      const travelerNum = idx >= 0 ? idx + 1 : selectedTravelerIds.length + 1;

      const existingIndex = currentAnswers.findIndex(
        (a) =>
          a.bookingQuestionId === bookingQuestionId &&
          a.travelerId === travelerId
      );

      const activityInfo = ctx[ParentState.activityInformation];
      const question = activityInfo?.travelerBookingQuestions?.find(
        (q: BookingQuestion) => q.id === bookingQuestionId
      );
      const unitFromShop = question?.units?.[0];
      const unitFallback: Partial<Record<BookingQuestionId, string>> = {
        [BookingQuestionId.Height]: "ft",
        [BookingQuestionId.Weight]: "lbs",
      };
      const unit = unitFromShop ?? unitFallback[bookingQuestionId];
      const answerEntry = {
        bookingQuestionId,
        answer,
        travelerNum,
        travelerId,
        ...(unit !== undefined && { unit }),
      };

      let updatedAnswers: TravelerBookingQuestionAnswer[];
      if (existingIndex >= 0) {
        updatedAnswers = [...currentAnswers];
        updatedAnswers[existingIndex] = answerEntry;
      } else {
        updatedAnswers = [...currentAnswers, answerEntry];
      }

      return setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.travelerBookingQuestionAnswers`,
        updatedAnswers
      );
    }
  ),
  setPrimaryParticipant: assign(
    (ctx: ActivitiesMachineContext, event: SetPrimaryParticipantEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.primaryParticipantId`,
        event.primaryParticipantId
      )
  ),
  setTransferArrivalMode: assign(
    (ctx: ActivitiesMachineContext, event: SetTransferArrivalModeEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.selectedTransferArrivalMode`,
        event.data
      )
  ),
  setTransferDepartureMode: assign(
    (ctx: ActivitiesMachineContext, event: SetTransferDepartureModeEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.selectedTransferDepartureMode`,
        event.data
      )
  ),
  setTransferArrivalOtherText: assign(
    (ctx: ActivitiesMachineContext, event: SetTransferArrivalOtherTextEvent) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.transferArrivalOtherText`,
        event.data
      )
  ),
  setTransferDepartureOtherText: assign(
    (
      ctx: ActivitiesMachineContext,
      event: SetTransferDepartureOtherTextEvent
    ) =>
      setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.transferDepartureOtherText`,
        event.data
      )
  ),
  setTransferConditionalAnswer: assign(
    (
      ctx: ActivitiesMachineContext,
      event: SetTransferConditionalAnswerEvent
    ) => {
      const currentAnswers =
        ctx[ParentState.activityInformation]?.transferConditionalAnswers ?? {};

      return setContextWithKey(
        ctx,
        `${ParentState.activityInformation}.transferConditionalAnswers`,
        {
          ...currentAnswers,
          [event.data.questionId]: event.data.answer,
        }
      );
    }
  ),
  populateTravelerBookingAnswers: assign((ctx: ActivitiesMachineContext) => {
    const activityInfo = ctx[ParentState.activityInformation];
    const travelerBookingQuestions = activityInfo?.travelerBookingQuestions;

    // No questions to populate
    if (!travelerBookingQuestions?.length) {
      return ctx;
    }

    const selectedTravelerIds =
      ctx[ParentState.travelerInformation]?.selectedTravelerIds ?? [];
    const travelers = ctx[ParentState.travelerInformation]?.travelers ?? [];

    if (!selectedTravelerIds.length) {
      return ctx;
    }

    const currentAnswers = activityInfo?.travelerBookingQuestionAnswers ?? [];

    const newAnswers: TravelerBookingQuestionAnswer[] = [...currentAnswers];

    // Populate answers for each selected traveler
    selectedTravelerIds.forEach((travelerId, index) => {
      const traveler = travelers.find((t) => t.id === travelerId);
      if (!traveler) {
        return;
      }

      // Skip this traveler if they already have user-entered answers (Weight/Height) to avoid data loss
      const travelerHasUserEnteredAnswers = currentAnswers.some(
        (a) =>
          a.travelerId === travelerId &&
          (a.bookingQuestionId === BookingQuestionId.Weight ||
            a.bookingQuestionId === BookingQuestionId.Height)
      );
      if (travelerHasUserEnteredAnswers) {
        return;
      }

      const travelerNum = index + 1;

      // Traveler may include airTravelerDetails.passport (from form/API)
      const passport = (
        traveler as {
          airTravelerDetails?: {
            passport?: {
              expiration?: string;
              countryOfIssue?: string;
              number?: string;
            };
          };
        }
      ).airTravelerDetails?.passport;

      travelerBookingQuestions.forEach((question: BookingQuestion) => {
        let answer: string | undefined;

        switch (question.id) {
          case BookingQuestionId.FullNamesFirst:
            answer = traveler.name?.first;
            break;
          case BookingQuestionId.FullNamesLast:
            answer = traveler.name?.last;
            break;
          case BookingQuestionId.DateOfBirth:
            answer = traveler.dateOfBirth;
            break;
          case BookingQuestionId.PassportExpiry:
            answer = passport?.expiration;
            break;
          case BookingQuestionId.PassportNationality:
            answer = passport?.countryOfIssue;
            break;
          case BookingQuestionId.PassportPassportNo:
            answer = passport?.number;
            break;
          case BookingQuestionId.Ageband:
          case BookingQuestionId.DisembarkationTime:
          case BookingQuestionId.Height:
          case BookingQuestionId.PickupPoint:
          case BookingQuestionId.SpecialRequirements:
          case BookingQuestionId.TransferAirArrivalAirline:
          case BookingQuestionId.TransferAirArrivalFlightNo:
          case BookingQuestionId.TransferAirDepartureAirline:
          case BookingQuestionId.TransferAirDepartureFlightNo:
          case BookingQuestionId.TransferArrivalDropOff:
          case BookingQuestionId.TransferArrivalMode:
          case BookingQuestionId.TransferArrivalTime:
          case BookingQuestionId.TransferDepartureDate:
          case BookingQuestionId.TransferDepartureMode:
          case BookingQuestionId.TransferDeparturePickup:
          case BookingQuestionId.TransferDepartureTime:
          case BookingQuestionId.TransferPortArrivalTime:
          case BookingQuestionId.TransferPortCruiseShip:
          case BookingQuestionId.TransferPortDepartureTime:
          case BookingQuestionId.TransferRailArrivalLine:
          case BookingQuestionId.TransferRailArrivalStation:
          case BookingQuestionId.TransferRailDepartureLine:
          case BookingQuestionId.TransferRailDepartureStation:
          case BookingQuestionId.Weight:
            // Not auto-populated from traveler data
            break;
          default:
            ensureExhaustive(question.id);
        }

        // Only add if this specific question hasn't been answered for this traveler yet
        const existingAnswer = currentAnswers.find(
          (a) =>
            a.travelerId === travelerId && a.bookingQuestionId === question.id
        );

        if (answer && !existingAnswer) {
          newAnswers.push({
            bookingQuestionId: question.id as BookingQuestionId,
            answer,
            travelerNum,
            travelerId,
          });
        }
      });
    });

    return setContextWithKey(
      ctx,
      `${ParentState.activityInformation}.travelerBookingQuestionAnswers`,
      newAnswers
    );
  }),
};

export const activitiesMachineId = "rbc-activities-checkout";

// RBC-specific state for activities checkout
export const ActivitiesCheckoutState = {
  primaryParticipantSelection: "primaryParticipantSelection",
  activityInformation: "activityInformation",
} as const;

const contactAndTraveller = getContactAndTravelerMachineState({
  parentMachineId: activitiesMachineId,
  nextState: `#${activitiesMachineId}.${ActivitiesCheckoutState.primaryParticipantSelection}`,
  nextStateFromReview: `#${activitiesMachineId}.${ActivitiesCheckoutState.primaryParticipantSelection}`,
  stateOverride: getRbcContactAndTravelerStateOverride({
    nextState: `#${activitiesMachineId}.${ActivitiesCheckoutState.primaryParticipantSelection}`,
    nextStateFromReview: `#${activitiesMachineId}.${ActivitiesCheckoutState.primaryParticipantSelection}`,
  }),
});

const contactAndTravelerWithActivityAnswers = {
  ...contactAndTraveller,
  on: {
    ...contactAndTraveller.on,
    [ActivityInformationEvent.SET_TRAVELER_BOOKING_QUESTION_ANSWER]: {
      actions: ActivityInformationActions.setTravelerBookingQuestionAnswer,
    },
  },
};

const states = {
  [ParentState.loading]: initializeState,
  [ParentState.contactAndTravelerInformation]:
    contactAndTravelerWithActivityAnswers,
  [ActivitiesCheckoutState.primaryParticipantSelection]: {
    initial: GenericChildState.idle,
    entry: ActivityInformationActions.populateTravelerBookingAnswers,
    always: {
      target: `#${activitiesMachineId}.${ActivitiesCheckoutState.activityInformation}`,
      cond: ActivityGuards.shouldSkipPrimaryParticipantSelection,
    },
    states: {
      [GenericChildState.idle]: {
        on: {
          [Event.NEXT]: `#${activitiesMachineId}.${ActivitiesCheckoutState.activityInformation}`,
          [Event.PREVIOUS]: `#${activitiesMachineId}.${ParentState.contactAndTravelerInformation}`,
          [ActivityInformationEvent.SET_PRIMARY_PARTICIPANT]: {
            actions: ActivityInformationActions.setPrimaryParticipant,
          },
        },
      },
    },
  },
  [ActivitiesCheckoutState.activityInformation]: {
    initial: GenericChildState.idle,
    states: {
      [GenericChildState.idle]: {
        on: {
          [Event.NEXT]: `#${activitiesMachineId}.${ParentState.cartQuote}`,
          [Event.PREVIOUS]: [
            {
              target: `#${activitiesMachineId}.${ActivitiesCheckoutState.primaryParticipantSelection}`,
              cond: ActivityGuards.requiresPrimaryParticipantSelection,
            },
            {
              target: `#${activitiesMachineId}.${ParentState.contactAndTravelerInformation}`,
            },
          ],
          [ActivityInformationEvent.SET_SELECTED_LANGUAGE_GUIDE]: {
            actions: ActivityInformationActions.setSelectedLanguageGuide,
          },
          [ActivityInformationEvent.SET_SPECIAL_REQUIREMENT]: {
            actions: ActivityInformationActions.setSpecialRequirement,
          },
          [ActivityInformationEvent.SET_SELECTED_PICKUP_POINT]: {
            actions: ActivityInformationActions.setSelectedPickupPoint,
          },
          [ActivityInformationEvent.SET_PICKUP_LOCATION_ANSWER]: {
            actions: ActivityInformationActions.setPickupLocationAnswer,
          },
          [ActivityInformationEvent.SET_CUSTOM_PICKUP_ADDRESS]: {
            actions: ActivityInformationActions.setCustomPickupAddress,
          },
          [ActivityInformationEvent.SET_TRAVELER_BOOKING_QUESTION_ANSWER]: {
            actions:
              ActivityInformationActions.setTravelerBookingQuestionAnswer,
          },
          [ActivityInformationEvent.SET_TRANSFER_ARRIVAL_MODE]: {
            actions: ActivityInformationActions.setTransferArrivalMode,
          },
          [ActivityInformationEvent.SET_TRANSFER_DEPARTURE_MODE]: {
            actions: ActivityInformationActions.setTransferDepartureMode,
          },
          [ActivityInformationEvent.SET_TRANSFER_ARRIVAL_OTHER_TEXT]: {
            actions: ActivityInformationActions.setTransferArrivalOtherText,
          },
          [ActivityInformationEvent.SET_TRANSFER_DEPARTURE_OTHER_TEXT]: {
            actions: ActivityInformationActions.setTransferDepartureOtherText,
          },
          [ActivityInformationEvent.SET_TRANSFER_CONDITIONAL_ANSWER]: {
            actions: ActivityInformationActions.setTransferConditionalAnswer,
          },
        },
      },
    },
  },
  [ParentState.cartQuote]: {
    entry: ActivitiesActions.addActivityProduct,
    ...getCartQuoteMachine({
      parentMachineId: activitiesMachineId,
      nextState: `#${activitiesMachineId}.${ParentState.cartUpdate}`,
      previousState: `#${activitiesMachineId}.${ActivitiesCheckoutState.activityInformation}`,
    }),
  },
  [ParentState.cartUpdate]: {
    ...getCartUpdateMachine({
      parentMachineId: activitiesMachineId,
      nextState: `#${activitiesMachineId}.${ParentState.cardPayment}`,
      previousState: `#${activitiesMachineId}.${ParentState.contactAndTravelerInformation}`,
    }),
    // TODO(Nate): Add Activities Action Types
    exit: LodgingActionTypes.checkForPriceChange,
  },
  [ParentState.cardPayment]: getCardPaymentMachine({
    parentMachineId: activitiesMachineId,
    nextState: `#${activitiesMachineId}.${ParentState.review}`,
    addNextState: `#${activitiesMachineId}.${ParentState.cardPayment}.${PaymentInformationState.idle}`,
    previousState: `#${activitiesMachineId}.${ParentState.contactAndTravelerInformation}`,
  }),
  [ParentState.awardsPayment]: getRewardsPaymentMachine({
    parentMachineId: activitiesMachineId,
    nextState: `#${activitiesMachineId}.${ParentState.cardPayment}`,
    previousState: `#${activitiesMachineId}.${ParentState.cartUpdate}`,
  }),
  [ParentState.review]: {
    initial: GenericChildState.idle,
    ...getReviewMachineState({
      parentMachineId: activitiesMachineId,
      nextState: `#${activitiesMachineId}.${ParentState.cartFulfill}`,
      previousState: `#${activitiesMachineId}.${ParentState.cardPayment}`,
      stateOverride: {
        [GenericChildState.idle]: getTermsAndConditionsMachineState,
      },
    }),
  },
  [ParentState.cartFulfill]: {
    entry: CardPaymentActionTypes.setPaymentFulfillParams,
    ...getCartFulfillMachine<ActivitiesMachineContext>({
      parentMachineId: activitiesMachineId,
      previousState: `#${activitiesMachineId}.${ParentState.review}`,
      nextState: `#${activitiesMachineId}.${ParentState.bookingConfirmation}`,
      trackPollSuccessAction: trackPollSuccess({
        eventName: COMPLETE_BUY_ACTIVITY,
        getAdditionalProperties: getCompleteBuyActivityAdditionalProperties,
      }),
      trackPollFailureAction: trackPollFailure({
        eventName: COMPLETE_BUY_ACTIVITY,
        getAdditionalProperties: getCompleteBuyActivityAdditionalProperties,
      }),
    }),
  },
  [ParentState.bookingConfirmation]: {
    entry: BookingConfirmationActionTypes.refreshPointsBalance,
  },
};

export const activitiesStateMachine: MachineConfig<
  ActivitiesMachineContext,
  any,
  TEvent
> = {
  schema: {
    context: getInitialActivitiesMachineContext(),
    events: {} as TEvent,
  },

  predictableActionArguments: true,
  // Machine identifier
  id: activitiesMachineId,

  // Initial state
  initial: ParentState.loading,

  // State definition
  states: states as any,
  on: {
    [Event.GO_TO_CONTACT_INFORMATION]: {
      actions: CartQuoteActionTypes.clearCartContext,
      target: `#${activitiesMachineId}.${ParentState.contactAndTravelerInformation}`,
    },
    [Event.GO_TO_ACTIVITY_INFORMATION]: {
      actions: CartQuoteActionTypes.clearCartContext,
      target: `#${activitiesMachineId}.${ActivitiesCheckoutState.activityInformation}`,
    },
    [Event.GO_TO_PRIMARY_PARTICIPANT]: {
      actions: CartQuoteActionTypes.clearCartContext,
      target: `#${activitiesMachineId}.${ActivitiesCheckoutState.primaryParticipantSelection}`,
    },
    [Event.GO_TO_CARD_PAYMENT]: `#${activitiesMachineId}.${ParentState.cardPayment}`,
    ...getRewardsPaymentMachine({
      parentMachineId: activitiesMachineId,
      nextState: `#${activitiesMachineId}.${ParentState.cardPayment}`,
      previousState: `#${activitiesMachineId}.${ParentState.cartUpdate}`,
    }).on,
    [Event.GO_TO_CART_FULFILL]: `#${activitiesMachineId}.${ParentState.cartFulfill}`,
  },
};
